import { useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import useInvalidateFetchAllProperties from 'components/domain/property/useInvalidateFetchAllProperties';
import useUnlinkSubUnitMutation from 'components/domain/property/useUnlinkSubUnitMutation';
import { modalIds } from 'components/modal/Modal.constants';
import { TextHighlightGreen } from 'components/text/TextHighlightGreen';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import { PropertyBase } from 'models/Properties';
import { PROPERTIES_FOR_PROPERTIES_BODY_BASE_QUERY_KEY } from 'pages/agencyProperties/body/useFetchPropertiesForPropertiesBody';

interface UnlinkSubUnitModalProps {
  masterUnit: Pick<PropertyBase, 'uid' | 'name'>;
  subUnit: Pick<PropertyBase, 'uid' | 'name'>;
}

const useUnlinkSubUnitModal = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { notifySuccess } = useNotify();
  const { openConfirmModal, closeModal, setErrorConfirmModal } = useAppModal();
  const { invalidateFetchAllProperties } = useInvalidateFetchAllProperties();

  const { mutateAsync: unlinkSubUnit } = useUnlinkSubUnitMutation({
    onSuccess: () => {
      closeModal(modalIds.confirm);
      queryClient.invalidateQueries([
        PROPERTIES_FOR_PROPERTIES_BODY_BASE_QUERY_KEY,
      ]);
      invalidateFetchAllProperties();
    },
    onError: (error: any) => {
      setErrorConfirmModal(error.response.data.apiErrorMessage);
    },
  });

  const openUnlinkSubUnitModal = ({
    masterUnit,
    subUnit,
  }: UnlinkSubUnitModalProps) => {
    openConfirmModal({
      body: (
        <Trans
          i18nKey="pageProperties.actions.unlinkSubUnitModal.header"
          components={{ highlight: <TextHighlightGreen /> }}
          values={{
            masterPropertyName: masterUnit.name,
            subUnitName: subUnit.name,
          }}
        />
      ),
      alertMessage: t('pageProperties.actions.unlinkSubUnitModal.alert'),
      onConfirmAsync: async () => {
        await unlinkSubUnit({
          masterPropertyUid: masterUnit.uid,
          subUnitUid: subUnit.uid,
        });

        const { name } = subUnit;

        notifySuccess(
          t('pageProperties.actions.unlinkSubUnitModal.unlinkSuccess', {
            name: name.length > 30 ? `${name.slice(0, 30)}...` : name,
          }),
        );
      },
      title: t('pageProperties.actions.common.areYouSure'),
      confirmLabel: t('pageProperties.actions.unlinkSubUnitModal.unlink'),
    });
  };

  return {
    openUnlinkSubUnitModal,
  };
};

export default useUnlinkSubUnitModal;
